import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { createForeignExchangeCurrencyOptions } from "./config";

export const filterForeignExchangeRateSchema = z.object({
  code_contains: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ]),
    )
    .optional(),
  name_contains: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ]),
    )
    .optional(),
  description_contains: z.string().nullable().optional(),
  currency_from: z.coerce.number().optional().nullable(),
  currency_to: z.coerce.number().optional().nullable(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterForeignExchangeRateSchema = z.infer<
  typeof filterForeignExchangeRateSchema
>;

const getFields = (t: (key: string) => string): Field[] => [
  {
    name: "code_contains",
    label: t("foreign_exchange_rate.filters.code"),
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/foreign_exchange_rates",
    column_name: "code",
  },
  {
    name: "name_contains",
    label: t("foreign_exchange_rate.filters.name"),
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/foreign_exchange_rates",
    column_name: "name",
  },
  {
    name: "description_contains",
    label: t("foreign_exchange_rate.filters.description"),
    type: "input",
    input_type: "text",
  },
  {
    name: "currency_from",
    label: t("foreign_exchange_rate.filters.currency_from"),
    type: "select",
    options: createForeignExchangeCurrencyOptions(),
    useTranslation: false,
  },
  {
    name: "currency_to",
    label: t("foreign_exchange_rate.filters.currency_to"),
    type: "select",
    options: createForeignExchangeCurrencyOptions(),
    useTranslation: false,
  },
  {
    name: "created_at_from",
    label: t("foreign_exchange_rate.filters.created_from"),
    type: "date",
  },
  {
    name: "created_at_to",
    label: t("foreign_exchange_rate.filters.created_to"),
    type: "date",
  },
];

export const client = "qtl - referential";

export function FilterForeignExchangeRate() {
  const { t } = useTranslation();
  const fields = getFields(t);

  return (
    <FilterTable
      client={client}
      schema={filterForeignExchangeRateSchema}
      tableName="foreign_exchange_rates"
      customBuildPath={() => `${client}/foreign_exchange_rates`}
      fields={fields}
    />
  );
}
